<template>
  <section class="gw-scope">
    <header class="gw-scope__head">
      <div class="gw-scope__title">
        <span class="gw-scope__title-icon">
          <v-icon size="16" color="#FFFFFF">{{
            icons.mdiCrosshairsGps
          }}</v-icon>
        </span>
        Périmètre de veille
      </div>
      <div class="gw-scope__head-right">
        <div v-if="page.originCount > 0" class="gw-scope__meta">
          {{ page.originCount }} origine(s) détectée(s)
        </div>

        <!-- Dans l'en-tête et non dans la grille de filtres : le support n'est
             pas un critère de plus, c'est le MODE de la page — il change les
             origines proposées, le tri, et ce que fait le bouton d'action.

             Même permission que la page « Créer post via transcription
             vidéo » : la vue vidéo appelle /source-preview et /video-angles,
             et ouvrir le toggle sans les endpoints montrerait un écran vide.
             Les quatre ont été ouverts ensemble le 2026-09-25. -->
        <media-type-segmented
          v-if="canWatchVideo"
          variant="on-dark"
          :disabled="searchDisabled"
        />
      </div>
    </header>

    <div class="gw-scope__grid">
      <div class="gw-scope__field gw-scope__field--legacy">
        <label class="rmr-table-shell__filter-label">Site</label>
        <site-filter
          site-list-getter="aiFactory/getSiteConnectors"
          item-value="site.id"
          item-text="site.name"
          store="aiFactory"
          store-getter="getSelectedSites"
          :multiple="false"
          :depends-on-site-groups="false"
          dense
        />
      </div>

      <div class="gw-scope__field gw-scope__field--legacy">
        <label class="rmr-table-shell__filter-label">Sources</label>
        <source-filter store="newsRadar" :exclude="externalSources" dense />
      </div>

      <div
        v-if="$can('read', 'ai_factory')"
        class="gw-scope__field gw-scope__field--legacy"
      >
        <label class="rmr-table-shell__filter-label">
          Configuration Full Auto
        </label>
        <aif-auto-config-filter :site="siteId" dense />
      </div>

      <origin-multi-select
        class="gw-scope__origins"
        :site-id="siteId"
        :source-ids="sourceIds"
        :dates="dates"
        @count-changed="page.originCount = $event"
      />

      <period-segmented class="gw-scope__period" />

      <!-- Sans objet en vidéo : une source vidéo est hors scoring, le seuil ne
           filtrerait rien. -->
      <div v-if="!isVideo" class="gw-scope__field gw-scope__score">
        <label class="rmr-table-shell__filter-label">
          Score min. — {{ scoreMin }}
        </label>
        <rmr-slider
          :value="scoreMin"
          :min="1"
          :max="10"
          :step="1"
          thumb-label
          class="gw-scope__slider"
          @input="setScoreMin"
        />
      </div>

      <rmr-action-button
        class="gw-scope__search"
        :class="{ 'gw-scope__search--wide': isVideo }"
        label="Rechercher"
        variant="primary"
        size="md"
        :disabled="searchDisabled"
        @click="applyFilters"
      />
    </div>
  </section>
</template>

<script>
import { computed, onMounted, reactive, watch } from "@vue/composition-api";
import { mdiCrosshairsGps } from "@mdi/js";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";

import SiteFilter from "@/components/common/filters/SiteFilter";
import AifAutoConfigFilter from "@/components/news-radar/common/filters/AifAutoConfigFilter.vue";
import SourceFilter from "@/components/news-radar/common/filters/SourceFilter.vue";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrSlider from "@/components/ui/forms/RmrSlider.vue";
import MediaTypeSegmented, {
  MEDIA_TYPE_WRITTEN,
} from "./parts/MediaTypeSegmented.vue";
import OriginMultiSelect from "./parts/OriginMultiSelect.vue";
import PeriodSegmented from "./parts/PeriodSegmented.vue";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";
import { NEWS_RADAR_EXTERNAL_SOURCES } from "@/utils/constants";
import { saveRmraCustomEvent } from "@/utils/rmr-analytics";

const DEFAULT_SCORE_MIN = 8;

export default {
  name: "WatchScopeBanner",
  components: {
    AifAutoConfigFilter,
    MediaTypeSegmented,
    OriginMultiSelect,
    PeriodSegmented,
    RmrActionButton,
    RmrSlider,
    SiteFilter,
    SourceFilter,
  },
  setup(props, { emit, root }) {
    const { isLoading } = useAxios();
    const { route, store } = useProxy();
    const { addQueryStringParam } = useQueryString();

    const page = reactive({
      isInitialized: false,
      originCount: 0,
    });

    // =>  COMPUTED
    const siteId = computed(() => {
      const value = store.getters["aiFactory/getSelectedSites"];
      return (isArray(value) && isEmpty(value)) || value === ""
        ? 0
        : Number(value) || 0;
    });

    const sourceIds = computed(
      () => store.getters["newsRadar/getSources"] || []
    );
    const dates = computed(() => store.getters["newsRadar/getDates"] || []);
    const aifAutoConfig = computed(
      () => store.getters["newsRadar/getAifAutoConfig"]
    );

    const scoreMin = computed(
      () => Number(store.getters["aiFactory/getScoreMin"]) || DEFAULT_SCORE_MIN
    );

    const searchDisabled = computed(
      () => siteId.value === 0 || isLoading.value
    );

    // Même garde que la page « Créer post via transcription vidéo » et que
    // ses endpoints : `ai_factory` / `read`, comme les autres pages de
    // création. Réservé au superadmin du 2026-08-26 au 2026-09-25.
    const canWatchVideo = computed(() => root.$can("read", "ai_factory"));

    const isVideo = computed(
      () => store.getters["newsRadar/getMediaType"] === "video"
    );

    // =>  METHODS
    const setScoreMin = (value) => {
      store.dispatch("aiFactory/updateScoreMin", value);

      // Same param name as the shared ScoreFilter, so deep-links keep working.
      if (page.isInitialized) {
        addQueryStringParam({ param: "scoreMin", value, is_integer: true });
      }
    };

    const applyFilters = () => {
      store.dispatch("common/updateApplyFilters", true);
      // Replicated from ApplyFilters.vue: dropping it would lose the analytics.
      saveRmraCustomEvent(route, "apply-filters");
      emit("search");
    };

    const applyAutoConfig = (config) => {
      if (config?.min_score !== null && config?.min_score !== undefined) {
        store.dispatch("aiFactory/updateScoreMin", Number(config.min_score));
      }
      if (!siteId.value && config?.site_id) {
        store.dispatch("aiFactory/updateSites", Number(config.site_id));
      }
    };

    // The shared Full Auto filter updates the News Radar store. This page keeps
    // its score in aiFactory, so mirror the selected configuration's threshold.
    watch(aifAutoConfig, (config, previousConfig) => {
      if (config) {
        applyAutoConfig(config);
      } else if (previousConfig) {
        store.dispatch("aiFactory/updateScoreMin", DEFAULT_SCORE_MIN);
      }
    });

    // A Full Auto configuration belongs to one site. Clear it when the user
    // switches site so stale config IDs cannot filter the new scope.
    watch(siteId, (nextSiteId, previousSiteId) => {
      if (
        previousSiteId &&
        nextSiteId !== previousSiteId &&
        aifAutoConfig.value
      ) {
        store.dispatch("newsRadar/updateAifAutoConfig", null);
      }
    });

    // =>  HOOKS
    onMounted(() => {
      if (aifAutoConfig.value) {
        applyAutoConfig(aifAutoConfig.value);
      }
      if (store.getters["aiFactory/getScoreMin"] === null) {
        store.dispatch("aiFactory/updateScoreMin", DEFAULT_SCORE_MIN);
      }
      // Le store survit à la navigation : sans ça, un utilisateur laissant la
      // veille en vidéo la rendrait inaccessible au suivant sur le même poste
      // s'il n'a pas la permission (EditoriaLink seul), toggle caché et liste
      // vide.
      if (!canWatchVideo.value) {
        store.dispatch("newsRadar/updateMediaType", MEDIA_TYPE_WRITTEN);
      }
      page.isInitialized = true;
    });

    // =>  DATA
    return {
      applyFilters,
      canWatchVideo,
      dates,
      isVideo,
      externalSources: NEWS_RADAR_EXTERNAL_SOURCES,
      icons: { mdiCrosshairsGps },
      page,
      scoreMin,
      searchDisabled,
      setScoreMin,
      siteId,
      sourceIds,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "./_global-watch.scss";

.gw-scope {
  background: linear-gradient(
    135deg,
    $rmr-indigo-dark 0%,
    $rmr-indigo-accent 45%,
    $rmr-indigo 100%
  );
  border-radius: $rmr-radius-3xl;
  padding: 18px 20px 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}

.gw-scope__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 4px 14px;
}

.gw-scope__title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: $rmr-surface-0;
  font-size: 15px;
  font-weight: 700;
}

.gw-scope__title-icon {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: $rmr-radius-sm;
  background: rgba(255, 255, 255, 0.2);
}

.gw-scope__head-right {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.gw-scope__meta {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.gw-scope__grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px;
  align-items: end;
  padding: 18px;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-0;
}

.gw-scope__field {
  grid-column: span 4;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.gw-scope__field--legacy {
  @include gw-legacy-field;
  @include gw-legacy-field-label;
}

// The slider has no box of its own: centre it in the shared control height so
// its cell bottom-aligns with the others.
.gw-scope__slider {
  display: flex;
  align-items: center;
  min-height: $gw-control-height;
}

.gw-scope__period {
  grid-column: span 3;
}

.gw-scope__origins {
  grid-column: span 4;
}

.gw-scope__score {
  grid-column: span 3;
}

.gw-scope__search {
  grid-column: span 2;
  width: 100%;
}

// Le seuil de score disparaît en vidéo : le bouton reprend sa place plutôt
// que de laisser un trou en fin de ligne.
.gw-scope__search--wide {
  grid-column: span 5;
}

@media (max-width: 1264px) {
  .gw-scope__grid {
    grid-template-columns: 1fr 1fr;
  }

  .gw-scope__field,
  .gw-scope__period,
  .gw-scope__origins,
  .gw-scope__score,
  .gw-scope__search {
    grid-column: span 1;
  }
}

@media (max-width: 700px) {
  .gw-scope__grid {
    grid-template-columns: 1fr;
  }
}
</style>
